import { HTTP_CAMPAIGNS } from '@/http';
import Api from './BaseApi';


export class CampaignsApi extends Api {
  /**
   * The constructor for the CampaignsApi.
   *
   * @param {Object} parameters The query parameters.
   */
  constructor(parameters = {}) {
    super(HTTP_CAMPAIGNS, `${process.env.API_MASSEMISSION_ROUTE}`, parameters);
    this.searchParameters = {};
  }

  /**
   * Method used to set the search parameters.
   *
   * @param {Object} param The given parameter.
   * @param {*} value The value to be set.
   *
   * @returns {CampaignsApi} The instance of the CampaignsApi.
   */
  setSearchParam(param, value) {
    this.searchParameters[param] = value;
    return this;
  }

  setSearchParamObject(params) {
    this.searchParameters = params;
    return this;
  }
  getSearchParam() {
    return this.searchParameters;
  }
  /**
   * Method used to fetch all items from the API.
   *
   * @returns {Promise} The result in a promise.
   */
  all() {
    return this.submit('post', `/${process.env.API_MASSEMISSION_LISTING_ROUTE}`, this.searchParameters);
  }

  getCampaign(id) {
    return this.submit('get', `/${this.endpoint}/${id}`);
  }

  getDispatchedDocs() {
    if (this.searchParameters.campaignId) {
      return this.submit('post', `/${this.endpoint}/${this.searchParameters.campaignId}/dispatched-docs`, this.searchParameters);
    }
    return null;
  }

  retryDispatch(id) {
    return this.submit('post', `/${this.endpoint}/retry/${id}`, {
      timeout: process.env.ARCHIVE_TIMEOUT,
    });
  }

  sendDispatch(id) {
    return this.submit('get', `/${this.endpoint}/validate/${id}`, {
      timeout: process.env.ARCHIVE_TIMEOUT,
    });
  }

  sendNeedDispatchingDispatch(id) {
    return this.submit('post', `/${this.endpoint}/send/${id}`, {
      timeout: process.env.ARCHIVE_TIMEOUT,
    });
  }


  destroyDispatchedDocs(dispatchedDocIds) {
    return this.submit('delete', `/${process.env.API_DISPATCHEDDOC_LISTING_ROUTE}`, { data: { dispatchedDocIds } });
  }

  retryDispatchedDoc(id) {
    return this.submit('post', `/${process.env.API_DISPATCHEDDOC_ROUTE}/retry/${id}`);
  }

  sendDispatchedDoc(id) {
    return this.submit('get', `/${process.env.API_DISPATCHEDDOC_ROUTE}/send/${id}`);
  }

  downloadFailedDocs(params) {
    let requestStatus = params.status;
    if (!params.status || !params.status.length) {
      requestStatus = undefined;
    }
    return this.submit('post', `/${this.endpoint}/${params.id}/dispatched-docs/merged`, { status: requestStatus }, {
      responseType: 'arraybuffer',
      headers: { Accept: 'application/octet-stream' },
      timeout: process.env.ARCHIVE_TIMEOUT,
    })
    .then(data => data);
  }

  downloadIgnoredDocs(id) {
    return this.submit('get', `/${this.endpoint}/${id}/ignored-pages`, {
      responseType: 'arraybuffer',
      headers: { Accept: 'application/octet-stream' },
      timeout: process.env.ARCHIVE_TIMEOUT,
    })
    .then(data => data);
  }

  /**
   * Method used to create a campaign.
   *
   * @param {Object} campaign The given campaign.
   *
   * @returns {Promise} The result in a promise.
   */
  create(campaign, siret, recipientsAuto, review, noDistribution) {
    const config = {
      headers: { 'content-type': 'multipart/form-data' },
      timeout: process.env.ARCHIVE_TIMEOUT,
    };
    const formData = new FormData();
    const docIds = [];
    const orgIds = [];
    const groupIds = [];

    if (campaign.documents.safebox) {
      for (let i = 0; i < campaign.documents.safebox.length; i += 1) {
        docIds.push(campaign.documents.safebox[i].id);
      }
    }

    if (campaign.recipients.thirdParties) {
      for (let i = 0; i < campaign.recipients.thirdParties.length; i += 1) {
        orgIds.push(campaign.recipients.thirdParties[i].thirdPartyOrg.id);
      }
    }
    if (campaign.recipients.groups) {
      for (let i = 0; i < campaign.recipients.groups.length; i += 1) {
        groupIds.push(campaign.recipients.groups[i].id);
      }
    }

    if (recipientsAuto === 'yes') {
      if (campaign.documents.computer) {
        formData.append('json',
          `{ "name": "${campaign.name}", "siret": "${siret}", "codeSignature": "${campaign.codeSignature}", "docIds": ${JSON.stringify(docIds)}, "groupIds" : ${JSON.stringify(groupIds)},"orgIds" : ${JSON.stringify(orgIds)}, "review": ${JSON.stringify(review)}, "noDistribution": ${JSON.stringify(noDistribution)}, "docTypeCode": "${campaign.documents.computer[0].docType}" }`,
        );
        for (let docIndex = 0; docIndex < campaign.documents.computer.length; docIndex += 1) {
          const formDataName = `"name": "${campaign.documents.computer[docIndex].name}"`;
          formData.append(`{ ${encodeURI(formDataName)} }`, campaign.documents.computer[docIndex]);
        }
      } else {
        formData.append('json',
          `{ "name": "${campaign.name}", "siret": "${siret}", "codeSignature": "${campaign.codeSignature}", "docIds": ${JSON.stringify(docIds)}, "groupIds" : ${JSON.stringify(groupIds)},"orgIds" : ${JSON.stringify(orgIds)}, "review": ${JSON.stringify(review)}, "noDistribution": ${JSON.stringify(noDistribution)} }`,
        );
      }
      return HTTP_CAMPAIGNS.post(`/${this.endpoint}`, formData, config)
        .then(data => data);
    }
    formData.append('json',
      `{ "name": "${campaign.name}", "siret": "${siret}", "codeSignature": "${campaign.codeSignature}", "docIds": ${JSON.stringify(docIds)}, "groupIds" : ${JSON.stringify(groupIds)},"orgIds" : ${JSON.stringify(orgIds)}, "review": ${JSON.stringify(review)}, "noDistribution": ${JSON.stringify(noDistribution)} }`,
    );
    if (campaign.documents.computer) {
      for (let docIndex = 0; docIndex < campaign.documents.computer.length; docIndex += 1) {
        const formDataName = `"name": "${campaign.documents.computer[docIndex].name}"`;
        const formDataDocType = `"docTypeCode": "${campaign.documents.computer[docIndex].docType}"`;
        formData.append(`{ ${encodeURI(formDataName)}, ${encodeURI(formDataDocType)}  }`, campaign.documents.computer[docIndex]);
      }
    }
    return HTTP_CAMPAIGNS.post(`/${this.endpoint}/manual`, formData, config)
      .then(data => data);
  }
}

export const campaignsApi = new CampaignsApi();



// WEBPACK FOOTER //
// ./src/api/CampaignsApi.js